// src/pages/Contactpersonen.js
// vCP-010
//
// ✅ Voornaam: automatisch hoofdletter(s) (ook met streepje: "ernst streepje jan" -> "Ernst-Jan")
// ✅ Voorletters: alleen hoofdletters (en netjes met puntjes)
// ✅ Geboortedatum: weer via spraak te zetten (YYYY-MM-DD / DD-MM-YYYY / "1 januari 1980")
// ✅ Huisnummer: cijfers (nummerwoorden -> cijfers waar mogelijk)
// ✅ Telefoon/Mobiel: alleen cijfers en '-' (geen spaties) + langere spreektijd (timeoutMs omhoog)
//
// NB: Opmerking blijft (zoals eerder afgesproken) achteraan toevoegen.

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

// ---------------- helpers ----------------
function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function safeTrim(s) {
  return String(s || "").trim();
}

function joinSp(parts, sep = " ") {
  return parts.map((x) => safeTrim(x)).filter(Boolean).join(sep).trim();
}

function upperInitialsFromFirstName(firstName) {
  const t = safeTrim(firstName);
  if (!t) return "";
  const parts = t.split(/\s+/).filter(Boolean);
  const letters = parts.map((p) => (p[0] ? p[0].toUpperCase() + "." : "")).filter(Boolean);
  return letters.join("");
}

function calcAgeLabel(isoYYYYMMDD) {
  const s = String(isoYYYYMMDD || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  const dob = new Date(y, m - 1, d);
  if (Number.isNaN(dob.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  const bd = (dob.getMonth() + 1) * 100 + dob.getDate();
  if (md < bd) age -= 1;
  if (age < 0) age = 0;
  return `${age} jaar`;
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  const companyCodeIds =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "x-tenant": tenant,
    "x-role": role,
    ...(companyCodeIds ? { "x-company-code-ids": companyCodeIds } : {}),
  };

  let lastErr;
  for (const url of urls) {
    try {
      const res = await fetch(buildUrl(url), { ...options, headers });
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}${msg ? ` - ${msg}` : ''}`);
      }
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) return await res.json();
      return await res.text();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Failed to fetch');
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function loadCrmMessageSettingsSafe(companyCodeId = "") {
  try {
    const qs = companyCodeId
      ? `?company_code_id=${encodeURIComponent(companyCodeId)}`
      : "";
    const data = await tryFetchJson([
      `/api/company-section-settings/crm_messages${qs}`,
    ]);

    return {
      email_subject: data?.settings?.email_subject || "Bericht van {bedrijfsnaam}",
      email_text: data?.settings?.email_text || `Geachte {contactpersoon},\n\n{bericht}\n\nMet vriendelijke groet,\n{verzender_naam}`,
      whatsapp_text: data?.settings?.whatsapp_text || `Beste {contactpersoon},\n\n{bericht}\n\nMet vriendelijke groet,\n{verzender_naam}`,
    };
  } catch {
    return {
      email_subject: "Bericht van {bedrijfsnaam}",
      email_text: `Geachte {contactpersoon},\n\n{bericht}\n\nMet vriendelijke groet,\n{verzender_naam}`,
      whatsapp_text: `Beste {contactpersoon},\n\n{bericht}\n\nMet vriendelijke groet,\n{verzender_naam}`,
    };
  }
}

function currentSenderName() {
  try {
    const user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
    return String(user?.display_name || user?.name || user?.email || "").trim();
  } catch {
    return "";
  }
}

function richTextToPlainText(value) {
  const raw = String(value || "");
  if (!raw) return "";

  try {
    const div = document.createElement("div");
    div.innerHTML = raw
      .replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n");
    return String(div.textContent || div.innerText || "")
      .replace(/\u00a0/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  } catch {
    return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
}

function fillCrmTemplate(template, context = {}) {
  const plain = richTextToPlainText(template);
  return plain.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(context, key)
      ? String(context[key] ?? "")
      : match;
  });
}


async function handleEmailGridClick(e, email, context = {}) {
  stopGridClick(e);

  const mail = String(email || "").trim();
  if (!mail || mail === "-") return;

  const communication = await loadCommunicationSettingsSafe();
  if (communication.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }


  const crm = await loadCrmMessageSettingsSafe(context.company_code_id || "");
  const values = {
    bedrijfsnaam: context.bedrijfsnaam || context.company_code_name || "StaffIQ",
    contactpersoon: context.contactpersoon || context.relatie || "",
    relatie: context.relatie || "",
    bericht: "",
    verzender_naam: currentSenderName(),
  };

  const subject = fillCrmTemplate(crm.email_subject, values);
  const body = fillCrmTemplate(crm.email_text, values);

  window.location.href = `mailto:${encodeURIComponent(mail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();
  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile, context = {}) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();
  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");
  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);
  const c = String(choice || "").trim().toLowerCase();
  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(`SMS naar ${raw}`, settings.default_sms_text || "");
    if (text === null) return;

    const message = String(text || "").trim();
    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: raw, text: message }),
      });
      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }
    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }


    const crm = await loadCrmMessageSettingsSafe(context.company_code_id || "");
    const values = {
      bedrijfsnaam: context.bedrijfsnaam || context.company_code_name || "StaffIQ",
      contactpersoon: context.contactpersoon || context.relatie || "",
      relatie: context.relatie || "",
      bericht: "",
      verzender_naam: currentSenderName(),
    };

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = fillCrmTemplate(crm.whatsapp_text, values);
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

function pickOrgName(o) {
  return o?.name || o?.naam || o?.bedrijfsnaam || o?.organisation_name || o?.organisatie_naam || o?.organisatie || "";
}

function pickContactFullName(c) {
  const fn = c?.first_name || c?.voornaam || "";
  const tv = c?.name_prefix || c?.tussenvoegsels || c?.tussenvoegsel || "";
  const ln = c?.last_name || c?.achternaam || "";
  const merged = joinSp([fn, tv, ln]);
  const full = safeTrim(c?.full_name || c?.naam || "");
  return merged || full || "";
}

function pickContactOrgName(c) {
  return c?.organisation_name || c?.organisatie_naam || c?.organisatie || "";
}

function pickItemCompanyCodeName(it) {
  return it?.company_code_name || it?.bedrijfscode_naam || it?.company_code || it?.bedrijfscode || "";
}

function pickOrgCompanyCodeName(o) {
  return o?.company_code_name || o?.bedrijfscode_naam || o?.company_code || o?.bedrijfscode || "";
}

function companyCodeNameForOrganisation(orgs, organisationId, fallback = "") {
  const wanted = String(organisationId || "");
  const org = (orgs || []).find((o) => String(o.id || "") === wanted);
  return pickOrgCompanyCodeName(org) || fallback || "";
}

function companyCodeIdForOrganisation(orgs, organisationId, fallback = "") {
  const wanted = String(organisationId || "");
  const org = (orgs || []).find((o) => String(o.id || "") === wanted);
  return org?.company_code_id || fallback || "";
}

function upsertById(arr, item) {
  const id = item?.id;
  if (id === null || id === undefined) return arr;
  const idx = arr.findIndex((x) => String(x.id) === String(id));
  if (idx === -1) return [item, ...arr];
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...item };
  return copy;
}

function removeById(arr, id) {
  return arr.filter((x) => String(x.id) !== String(id));
}

function genderLabel(g) {
  const x = String(g || "").toLowerCase();
  if (x === "m") return "m";
  if (x === "v") return "v";
  if (x === "o") return "o";
  return "";
}

// ✅ Zoeknaam: "Achternaam, Voornaam tussenvoegsels"
function computeZoeknaam({ last_name, name_prefix, first_name }) {
  const ln = safeTrim(last_name);
  const fn = safeTrim(first_name);
  const tv = safeTrim(name_prefix);
  const right = joinSp([fn, tv]);
  return joinSp([ln ? `${ln},` : "", right]);
}

// ✅ Aanhef: Geachte heer/mevrouw + tussenvoegsels casing zoals ingevoerd
function computeAanhef({ gender, name_prefix, last_name }) {
  const g = String(gender || "").toLowerCase();
  const tvRaw = safeTrim(name_prefix);
  const ln = safeTrim(last_name);

  const tv =
    tvRaw ? tvRaw.charAt(0).toUpperCase() + tvRaw.slice(1).toLowerCase() : "";

  let prefix = "Geachte";
  if (g === "m") prefix = "Geachte heer";
  else if (g === "v") prefix = "Geachte mevrouw";

  return joinSp([prefix, tv, ln]);
}

// ✅ Aanschrijving: T.a.v. de heer/mevrouw + voorletters + tussenvoegsels + achternaam
function computeAanschrijving({ gender, initials, name_prefix, last_name }) {
  const g = String(gender || "").toLowerCase();
  let ta = "T.a.v.";
  if (g === "m") ta = "T.a.v. de heer";
  else if (g === "v") ta = "T.a.v. mevrouw";

  const vl = safeTrim(initials);
  const tv = safeTrim(name_prefix);
  const ln = safeTrim(last_name);
  return joinSp([ta, vl, tv, ln]);
}

// -------- keuzelijst opslag in localStorage --------
function loadLocalList(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean).map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

function saveLocalList(key, arr) {
  try {
    const clean = Array.from(new Set((arr || []).map((x) => String(x).trim()).filter(Boolean)));
    localStorage.setItem(key, JSON.stringify(clean));
  } catch {}
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function ensureOption(key, value) {
  const v = String(value || "").trim();
  if (!v) return;
  const cur = loadLocalList(key);
  if (!cur.some((x) => norm(x) === norm(v))) {
    saveLocalList(key, [...cur, v]);
  }
}

// ✅ Visit-address opslaan als gelabelde bloktekst in visit_address
function buildVisitAddressText(f) {
  const lines = [
    "Bezoekadres:",
    `Straat: ${safeTrim(f.visit_street)}`,
    `Huisnummer: ${safeTrim(f.visit_house_number)}`,
    `Toevoeging: ${safeTrim(f.visit_addition)}`,
    `Postcode: ${safeTrim(f.visit_postcode).toUpperCase().replace(/\s+/g, "")}`,
    `Plaats: ${safeTrim(f.visit_city)}`,
    `Provincie: ${safeTrim(f.visit_province)}`,
    `Land: ${safeTrim(f.visit_country)}`,
  ];

  const hasAny =
    safeTrim(f.visit_street) ||
    safeTrim(f.visit_house_number) ||
    safeTrim(f.visit_addition) ||
    safeTrim(f.visit_postcode) ||
    safeTrim(f.visit_city) ||
    safeTrim(f.visit_province) ||
    safeTrim(f.visit_country);

  return hasAny ? lines.join("\n").trim() : "";
}

function tryParseVisitAddressText(text) {
  const t = String(text || "").trim();
  if (!t) return null;

  const lines = t.split("\n").map((x) => x.trim());
  const getVal = (label) => {
    const line = lines.find((x) => norm(x).startsWith(norm(label) + ":"));
    if (!line) return "";
    const idx = line.indexOf(":");
    return idx >= 0 ? line.slice(idx + 1).trim() : "";
  };

  const straat = getVal("Straat");
  const huisnr = getVal("Huisnummer");
  const toevoeg = getVal("Toevoeging");
  const pc = getVal("Postcode");
  const plaats = getVal("Plaats");
  const prov = getVal("Provincie");
  const land = getVal("Land");

  const hasLabeled =
    straat || huisnr || toevoeg || pc || plaats || prov || land || lines.some((x) => norm(x) === "bezoekadres:");

  if (!hasLabeled) return null;

  return {
    visit_street: straat,
    visit_house_number: huisnr,
    visit_addition: toevoeg,
    visit_postcode: pc,
    visit_city: plaats,
    visit_province: prov,
    visit_country: land,
  };
}

function pickVisitAddressRaw(obj) {
  return (
    obj?.visit_address ||
    obj?.visitAddress ||
    obj?.visit_address_text ||
    obj?.visitAddressText ||
    obj?.visitAddressRaw ||
    obj?.visitaddress ||
    obj?.visit_adres ||
    obj?.visitadres ||
    ""
  );
}

// ✅ Actief parsing/label
function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  const s = String(raw).toLowerCase().trim();
  if (raw === true) return true;
  if (raw === false) return false;
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee") return false;
  return !!raw;
}

function parseYesNo(raw) {
  const s = norm(raw);
  if (!s) return null;
  if (["1", "true", "t", "yes", "y", "j", "ja", "aan", "on"].includes(s)) return true;
  if (["0", "false", "f", "no", "n", "nee", "uit", "off"].includes(s)) return false;
  return null;
}

// ---------------- NEW: spraak normalizers ----------------
function replaceStreepjeWords(raw) {
  // "ernst streepje jan" => "ernst-jan" (ook "streepje" "minus" "dash")
  return String(raw || "")
    .replace(/\b(streepje|min|minus|dash)\b/gi, "-")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function capitalizeWord(w) {
  const t = String(w || "");
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

function formatFirstName(raw) {
  const s0 = replaceStreepjeWords(raw);
  if (!s0) return "";
  // behoud spaties én streepjes, maar cap per segment
  const parts = s0.split(" ").filter(Boolean);
  const capParts = parts.map((p) =>
    p
      .split("-")
      .map((x) => capitalizeWord(x))
      .join("-")
  );
  return capParts.join(" ");
}

function formatInitials(raw) {
  const s = String(raw || "").toUpperCase().replace(/[^A-Z.]/g, "");
  const letters = s.replace(/\./g, "").split("").filter(Boolean).slice(0, 10);
  if (!letters.length) return "";
  return letters.map((c) => `${c}.`).join("");
}

const nlMonths = {
  januari: 1,
  jan: 1,
  februari: 2,
  feb: 2,
  maart: 3,
  mrt: 3,
  april: 4,
  apr: 4,
  mei: 5,
  juni: 6,
  jun: 6,
  juli: 7,
  jul: 7,
  augustus: 8,
  aug: 8,
  september: 9,
  sep: 9,
  oktober: 10,
  okt: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

function pad2(n) {
  const x = String(n);
  return x.length === 1 ? "0" + x : x;
}

function parseBirthDateToISO(raw) {
  const s0 = String(raw || "").trim();
  if (!s0) return null;

  // YYYY-MM-DD
  const m1 = s0.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m1) {
    const y = Number(m1[1]);
    const m = Number(m1[2]);
    const d = Number(m1[3]);
    if (y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // DD-MM-YYYY
  const m2 = s0.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m2) {
    const d = Number(m2[1]);
    const m = Number(m2[2]);
    const y = Number(m2[3]);
    if (y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // "1 januari 1980" / "01 jan 1980"
  const t = norm(s0);
  const m3 = t.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/);
  if (m3) {
    const d = Number(m3[1]);
    const mon = nlMonths[m3[2]] || 0;
    const y = Number(m3[3]);
    if (y >= 1900 && y <= 2100 && mon >= 1 && mon <= 12 && d >= 1 && d <= 31) return `${y}-${pad2(mon)}-${pad2(d)}`;
  }

  return null;
}

const nlNums = {
  nul: 0,
  een: 1,
  één: 1,
  twee: 2,
  drie: 3,
  vier: 4,
  vijf: 5,
  zes: 6,
  zeven: 7,
  acht: 8,
  negen: 9,
  tien: 10,
  elf: 11,
  twaalf: 12,
  dertien: 13,
  veertien: 14,
  vijftien: 15,
  zestien: 16,
  zeventien: 17,
  achttien: 18,
  negentien: 19,
  twintig: 20,
  dertig: 30,
  veertig: 40,
  vijftig: 50,
  zestig: 60,
  zeventig: 70,
  tachtig: 80,
  negentig: 90,
  honderd: 100,
  duizend: 1000,
};

function parseDutchNumberWordsToInt(raw) {
  // simpele NL-parser (werkt goed voor huisnummers tot 9999-ish)
  const t = norm(raw)
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return null;

  // als er al cijfers inzitten: pak die
  const digits = t.replace(/[^\d]/g, "");
  if (digits) return Number(digits);

  const parts = t.split(" ").filter(Boolean);
  let total = 0;
  let current = 0;
  let used = false;

  for (const p of parts) {
    const v = nlNums[p];
    if (v === undefined) continue;
    used = true;

    if (v === 100) {
      current = (current || 1) * 100;
    } else if (v === 1000) {
      total += (current || 1) * 1000;
      current = 0;
    } else if (v >= 20) {
      current += v;
    } else {
      current += v;
    }
  }

  if (!used) return null;
  return total + current;
}

function sanitizeHouseNumber(raw) {
  const n = parseDutchNumberWordsToInt(raw);
  if (Number.isFinite(n)) return String(n);
  // fallback: alleen digits uit raw
  const digits = String(raw || "").replace(/[^\d]/g, "");
  return digits || "";
}

function sanitizePhone(raw) {
  const s0 = replaceStreepjeWords(raw);
  return s0.replace(/[^\d+\-\s]/g, "");
}
function pickOrgPhone(o) {
  return o?.phone || o?.telefoon || o?.telefoonnummer || "";
}

function pickOrgMobile(o) {
  return o?.mobile || o?.mobiel || o?.mobile_number || o?.mobiel_nummer || o?.mobielnummer || "";
}

function pickOrgEmail(o) {
  return o?.email || o?.e_mail || "";
}

function pickOrgWebsite(o) {
  return o?.website || o?.internet || o?.internetadres || o?.internet_adres || "";
}

function isCompanyRelation(o) {
  return String(o?.relation_entity_type || "company").toLowerCase() !== "private";
}

function mapOrgToContactDefaults(o) {
  if (!o) return {};

  return {
    // bezoekadres
    visit_street: o.visit_street || "",
    visit_house_number: o.visit_house_number || "",
    visit_addition: o.visit_addition || "",
    visit_postcode: o.visit_postcode || "",
    visit_city: o.visit_city || "",
    visit_province: o.visit_province || "",
    visit_country: o.visit_country || "",

    // correspondentie
    corr_street: o.corr_street || "",
    corr_house_number: o.corr_house_number || "",
    corr_addition: o.corr_addition || "",
    corr_postcode: o.corr_postcode || "",
    corr_city: o.corr_city || "",
    corr_province: o.corr_province || "",
    corr_country: o.corr_country || "",

    // contact
    phone: pickOrgPhone(o),
    mobile: pickOrgMobile(o),
    email: pickOrgEmail(o),
    website: pickOrgWebsite(o),

    // flags
    invoice_digital: !!(o.invoice_digital ?? o.facturen_digitaal ?? o.digital_invoice),
    mailing_list: !!(o.mailing_list ?? o.mailinglijst ?? o.mailing),
  };
}
// ---------------- component ----------------
const emptyForm = {
  id: null,

  // naam
  first_name: "",
  initials: "",
  name_prefix: "",
  last_name: "",
  search_name: "",
  title_before: "",
  title_after: "",
  gender: "",
  birth_date: "",

  // org + werk
  organisation_id: "",
  company_code_id: "",
  department: "",
  job_function: "",

  // aanhef/aanschrijving
  salutation: "",
  attention: "",

  // bezoekadres
  visit_street: "",
  visit_house_number: "",
  visit_addition: "",
  visit_postcode: "",
  visit_city: "",
  visit_province: "",
  visit_country: "",

  // correspondentie
  corr_street: "",
  corr_house_number: "",
  corr_addition: "",
  corr_postcode: "",
  corr_city: "",
  corr_province: "",
  corr_country: "",

  // contact
  phone: "",
  mobile: "",
  email: "",
  website: "",

  // flags
  invoice_digital: false,
  mailing_list: false,
  is_primary: false,

  // actief
  active: true,

  // opmerking
  remarks: "",
};

export default function Contactpersonen() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonly = String(role || "").toLowerCase() === "readonly";
  const VERSION = "vCP-010";

  // data
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);

  // selectie + form
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // "handmatig aangepast?" flags voor voorstellen
  const [touched, setTouched] = useState({
    search_name: false,
    salutation: false,
    attention: false,
  });

  // corrTouched: alleen voor NIEUW contact bezoek->corr kopie
  const [corrTouched, setCorrTouched] = useState({
    corr_street: false,
    corr_house_number: false,
    corr_addition: false,
    corr_postcode: false,
    corr_city: false,
    corr_province: false,
    corr_country: false,
  });

  // cursor index
  const [listIndex, setListIndex] = useState(-1);

  // ui status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");

  const [reloadKey, setReloadKey] = useState(0);
  const [speechInfo, setSpeechInfo] = useState("");

  const [hideVoiceError, setHideVoiceError] = useState(false);
  const prevVoiceErrorRef = useRef("");

  const [optionsTick, setOptionsTick] = useState(0);

  // na spraak-zoek cursor naar 1e resultaat
  const [pendingSpeechSearch, setPendingSpeechSearch] = useState(false);

  // zoek + filters
  const [q, setQ] = useState(getParam("q"));
  const [orgFilterId, setOrgFilterId] = useState(getParam("organisation_id") || "");

  // dropdown filters (grid)
  const EMPTY_VAL = "__EMPTY__";
  const [nameFilter, setNameFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j"); // default actief
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

const [mode, setMode] = useState("search");
const isFormLocked = isReadonly || mode !== "form";

const [sort, setSort] = useState({ key: "search_name", dir: "asc" });

  // refs
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const reopenContactHandledRef = useRef(false);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

  const firstNameRef = useRef(null);
  const initialsRef = useRef(null);
  const prefixRef = useRef(null);
  const lastNameRef = useRef(null);
  const searchNameRef = useRef(null);
  const genderRef = useRef(null);
  const birthRef = useRef(null);
  const salutationRef = useRef(null);
  const attentionRef = useRef(null);

  const orgRef = useRef(null);
  const lastAutoFillOrgIdRef = useRef("");
  const deptRef = useRef(null);
  const jobRef = useRef(null);

  const vStreetRef = useRef(null);
  const vHouseRef = useRef(null);
  const vAddRef = useRef(null);
  const vPcRef = useRef(null);
  const vCityRef = useRef(null);
  const vProvRef = useRef(null);
  const vCountryRef = useRef(null);

  const cStreetRef = useRef(null);
  const cHouseRef = useRef(null);
  const cAddRef = useRef(null);
  const cPcRef = useRef(null);
  const cCityRef = useRef(null);
  const cProvRef = useRef(null);
  const cCountryRef = useRef(null);

  const phoneRef = useRef(null);
  const mobileRef = useRef(null);
  const emailRef = useRef(null);
  const webRef = useRef(null);

  // checkbox refs
  const invoiceRef = useRef(null);
  const mailingRef = useRef(null);
  const primaryRef = useRef(null);
  const activeRef = useRef(null);

  const [focusedKey, setFocusedKey] = useState("");

  const [remarkEditor, setRemarkEditor] = useState(false);
  const remarkBigRef = useRef(null);
  const remarkSmallRef = useRef(null);

  // remark dicteer-mode: alleen binnen remarkEditor
  const remarkDictateActiveRef = useRef(false);
  const remarkDictateRunsRef = useRef(0);
  const ignoreNextRemarkSmallFocusRef = useRef(false);

  // ---- keuzelijsten ----
  const deptOptionsKey = "cp_options_department";
  const jobOptionsKey = "cp_options_job_function";
  const pcOptionsKey = "cp_options_postcode";
  const cityOptionsKey = "cp_options_city";
  const provOptionsKey = "cp_options_province";
  const countryOptionsKey = "cp_options_country";

  const derivedDept = useMemo(() => uniqSorted(items.map((x) => x.department || "")), [items]);
  const derivedJob = useMemo(() => uniqSorted(items.map((x) => x.job_function || "")), [items]);

  const derivedVisit = useMemo(() => {
    const parsed = items.map((x) => tryParseVisitAddressText(pickVisitAddressRaw(x))).filter(Boolean);
    return {
      pc: uniqSorted(parsed.map((p) => p.visit_postcode || "")),
      city: uniqSorted(parsed.map((p) => p.visit_city || "")),
      prov: uniqSorted(parsed.map((p) => p.visit_province || "")),
      country: uniqSorted(parsed.map((p) => p.visit_country || "")),
    };
  }, [items]);

  const derivedPC = useMemo(() => uniqSorted([...items.map((x) => x.corr_postcode || ""), ...derivedVisit.pc]), [items, derivedVisit.pc]);
  const derivedCity = useMemo(() => uniqSorted([...items.map((x) => x.corr_city || ""), ...derivedVisit.city]), [items, derivedVisit.city]);
  const derivedProv = useMemo(() => uniqSorted([...items.map((x) => x.corr_province || ""), ...derivedVisit.prov]), [items, derivedVisit.prov]);
  const derivedCountry = useMemo(() => uniqSorted([...items.map((x) => x.corr_country || ""), ...derivedVisit.country]), [items, derivedVisit.country]);

  const departmentOptions = useMemo(() => uniqSorted([...derivedDept, ...loadLocalList(deptOptionsKey)]), [derivedDept, optionsTick]);
  const jobFunctionOptions = useMemo(() => uniqSorted([...derivedJob, ...loadLocalList(jobOptionsKey)]), [derivedJob, optionsTick]);
  const postcodeOptions = useMemo(() => uniqSorted([...derivedPC, ...loadLocalList(pcOptionsKey)]), [derivedPC, optionsTick]);
  const cityOptions = useMemo(() => uniqSorted([...derivedCity, ...loadLocalList(cityOptionsKey)]), [derivedCity, optionsTick]);
  const provinceOptions = useMemo(() => uniqSorted([...derivedProv, ...loadLocalList(provOptionsKey)]), [derivedProv, optionsTick]);
  const countryOptions = useMemo(() => uniqSorted([...derivedCountry, ...loadLocalList(countryOptionsKey)]), [derivedCountry, optionsTick]);

  // dropdown-opties midden-grid
const nameOptions = useMemo(() =>
  uniqSorted(items.map((x) => x.search_name || "")),
  [items]
);
  const nameHasEmpty = useMemo(() => items.some((x) => !safeTrim(pickContactFullName(x))), [items]);

  const jobColOptions = useMemo(() => uniqSorted(items.map((x) => x.job_function || "")), [items]);
  const jobHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.job_function || "")), [items]);

  const phoneColOptions = useMemo(() => uniqSorted(items.map((x) => x.phone || "")), [items]);
  const phoneHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.phone || "")), [items]);

const mobileColOptions = useMemo(() => uniqSorted(items.map((x) => x.mobile || "")), [items]);
const mobileHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.mobile || "")), [items]);

  const emailColOptions = useMemo(() => uniqSorted(items.map((x) => x.email || "")), [items]);
  const emailHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.email || "")), [items]);

  const companyCodeColOptions = useMemo(() => uniqSorted(items.map((x) => pickItemCompanyCodeName(x))), [items]);
  const companyCodeHasEmpty = useMemo(() => items.some((x) => !safeTrim(pickItemCompanyCodeName(x))), [items]);

  function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

  function computeTouchedForLoadedForm(f) {
    const autoSearch = computeZoeknaam(f);
    const autoSal = computeAanhef(f);
    const autoAtt = computeAanschrijving(f);
    const eq = (a, b) => norm(a) === norm(b);

    return {
      search_name: !!safeTrim(f.search_name) && !eq(f.search_name, autoSearch),
      salutation: !!safeTrim(f.salutation) && !eq(f.salutation, autoSal),
      attention: !!safeTrim(f.attention) && !eq(f.attention, autoAtt),
    };
  }

  function loadItemFromRowSync(it) {
    setSelectedId(it.id);

    const actParsed = parseActiveValue(it);
    const act = actParsed === null ? true : !!actParsed;

    const f = {
      ...emptyForm,
      id: it.id,

      first_name: it.first_name || "",
      initials: it.initials || "",
      name_prefix: it.name_prefix || it.tussenvoegsels || it.tussenvoegsel || "",
      last_name: it.last_name || "",
      search_name: it.search_name || "",
      title_before: it.title_before || "",
      title_after: it.title_after || "",
      gender: it.gender || "",
      birth_date: (it.birth_date || "").toString().slice(0, 10),

      organisation_id: it.organisation_id ? String(it.organisation_id) : "",
      company_code_id: it.company_code_id ? String(it.company_code_id) : "",
      department: it.department || "",
      job_function: it.job_function || "",

      salutation: it.salutation || "",
      attention: it.attention || "",

      corr_street: it.corr_street || "",
      corr_house_number: it.corr_house_number || "",
      corr_addition: it.corr_addition || "",
      corr_postcode: it.corr_postcode || "",
      corr_city: it.corr_city || "",
      corr_province: it.corr_province || "",
      corr_country: it.corr_country || "",

      phone: it.phone || "",
      mobile: it.mobile || "",
      email: it.email || "",
      website: it.website || "",

      invoice_digital: !!it.invoice_digital,
      mailing_list: !!it.mailing_list,
      is_primary: !!it.is_primary,

      active: act,

      remarks: it.remarks || "",
    };

    const fromCols = {
      visit_street: it.visit_street || "",
      visit_house_number: it.visit_house_number || "",
      visit_addition: it.visit_addition || "",
      visit_postcode: it.visit_postcode || "",
      visit_city: it.visit_city || "",
      visit_province: it.visit_province || "",
      visit_country: it.visit_country || "",
    };

    const hasCols =
      safeTrim(fromCols.visit_street) ||
      safeTrim(fromCols.visit_house_number) ||
      safeTrim(fromCols.visit_addition) ||
      safeTrim(fromCols.visit_postcode) ||
      safeTrim(fromCols.visit_city) ||
      safeTrim(fromCols.visit_province) ||
      safeTrim(fromCols.visit_country);

    const parsedVisit = tryParseVisitAddressText(pickVisitAddressRaw(it));
    const f2 = hasCols ? { ...f, ...fromCols } : parsedVisit ? { ...f, ...parsedVisit } : f;

    setForm(f2);
    setTouched(computeTouchedForLoadedForm(f2));

    setCorrTouched({
      corr_street: true,
      corr_house_number: true,
      corr_addition: true,
      corr_postcode: true,
      corr_city: true,
      corr_province: true,
      corr_country: true,
    });

    setSaveError(null);
    setFlash("");

    remarkDictateActiveRef.current = false;
    remarkDictateRunsRef.current = 0;
    setRemarkEditor(false);
  }

  async function loadItemFromRow(it) {
    loadItemFromRowSync(it);

    const hasAnyVisitCols =
      safeTrim(it?.visit_street) ||
      safeTrim(it?.visit_house_number) ||
      safeTrim(it?.visit_addition) ||
      safeTrim(it?.visit_postcode) ||
      safeTrim(it?.visit_city) ||
      safeTrim(it?.visit_province) ||
      safeTrim(it?.visit_country);

    const hasVisitInList = !!safeTrim(pickVisitAddressRaw(it)) || !!hasAnyVisitCols;
    if (hasVisitInList) return;

    const id = it?.id;
    if (id === null || id === undefined || String(id) === "") return;

    try {
      const detail = await tryFetchJson([`/api/contacts/${id}`]);
      if (detail && typeof detail === "object") {
        const hasCols =
          safeTrim(detail?.visit_street) ||
          safeTrim(detail?.visit_house_number) ||
          safeTrim(detail?.visit_addition) ||
          safeTrim(detail?.visit_postcode) ||
          safeTrim(detail?.visit_city) ||
          safeTrim(detail?.visit_province) ||
          safeTrim(detail?.visit_country);

        if (safeTrim(pickVisitAddressRaw(detail)) || hasCols) {
          loadItemFromRowSync(detail);
          setItems((prev) => upsertById(prev, detail));
        }
      }
    } catch {}
  }

  // ---------- AUTO VOORSTELLEN (LIVE) ----------
  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!safeTrim(next.initials) && safeTrim(next.first_name)) {
        next.initials = upperInitialsFromFirstName(next.first_name);
        changed = true;
      }

      if (!touched.search_name) {
        const proposal = computeZoeknaam(next);
        if (proposal && proposal !== next.search_name) {
          next.search_name = proposal;
          changed = true;
        }
      }

      if (!touched.salutation) {
        const proposal = computeAanhef(next);
        if (proposal && proposal !== next.salutation) {
          next.salutation = proposal;
          changed = true;
        }
      }

      if (!touched.attention) {
        const proposal = computeAanschrijving(next);
        if (proposal && proposal !== next.attention) {
          next.attention = proposal;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.first_name, form.last_name, form.name_prefix, form.gender, form.initials, touched.search_name, touched.salutation, touched.attention]);

  // NIEUW contact -> bezoekadres live kopiëren naar correspondentieadres
  useEffect(() => {
    if (formHasId()) return;

    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      const copyIfAllowed = (visitKey, corrKey) => {
        if (corrTouched[corrKey]) return;
        const v = prev[visitKey] || "";
        if ((prev[corrKey] || "") !== v) {
          next[corrKey] = v;
          changed = true;
        }
      };

      copyIfAllowed("visit_street", "corr_street");
      copyIfAllowed("visit_house_number", "corr_house_number");
      copyIfAllowed("visit_addition", "corr_addition");
      copyIfAllowed("visit_postcode", "corr_postcode");
      copyIfAllowed("visit_city", "corr_city");
      copyIfAllowed("visit_province", "corr_province");
      copyIfAllowed("visit_country", "corr_country");

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.id,
    form.visit_street,
    form.visit_house_number,
    form.visit_addition,
    form.visit_postcode,
    form.visit_city,
    form.visit_province,
    form.visit_country,
    corrTouched.corr_street,
    corrTouched.corr_house_number,
    corrTouched.corr_addition,
    corrTouched.corr_postcode,
    corrTouched.corr_city,
    corrTouched.corr_province,
    corrTouched.corr_country,
  ]);

// NIEUW contact -> bij kiezen van organisatie adresgegevens eerst overnemen uit organisatie
useEffect(() => {
  if (formHasId()) {
    lastAutoFillOrgIdRef.current = "";
    return;
  }

  const oid = String(form.organisation_id || "");
  if (!oid) {
    lastAutoFillOrgIdRef.current = "";
    return;
  }

  if (lastAutoFillOrgIdRef.current === oid) return;

  const org = orgs.find((o) => String(o.id) === oid);
  if (!org) return;

  lastAutoFillOrgIdRef.current = oid;

  const mapped = mapOrgToContactDefaults(org);

  setForm((prev) => ({
    ...prev,
    organisation_id: oid,
    company_code_id: companyCodeIdForOrganisation(orgs, oid, prev.company_code_id),
    ...mapped,
  }));
}, [form.organisation_id, orgs]);

// ---------- LOAD RELATIES ----------
  // Contactpersonen mogen alleen aan Organisaties/bedrijven worden gekoppeld.
  // Particulieren worden hier niet getoond.
  useEffect(() => {
    let cancelled = false;
    async function loadOrgs() {
      try {
        const orgData = await tryFetchJson(["/api/organisations?q=", "/api/organisations"]);
        const arr = Array.isArray(orgData) ? orgData : [];
        const companiesOnly = arr.filter((o) => isCompanyRelation(o));

        if (!cancelled) setOrgs(companiesOnly);
      } catch {
        if (!cancelled) setOrgs([]);
      }
    }
    loadOrgs();
    return () => (cancelled = true);
  }, []);

  // ---------- LOAD CONTACTS ----------
  const serverQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (orgFilterId) p.set("organisation_id", orgFilterId);
    if (activeFilter === "j") p.set("active", "1");
    else if (activeFilter === "n") p.set("active", "0");
    return p.toString();
  }, [q, orgFilterId, activeFilter]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setError(null);
      try {
        const data = await tryFetchJson([`/api/contacts?${serverQuery}`, "/api/contacts"]);
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [serverQuery, reloadKey]);

  // ---------- FILTER + SORT ----------
  const filteredItems = useMemo(() => {
    const qq = norm(q);
    const arr = items.slice();

    const byQ = (it) => {
      if (!qq) return true;
      const hay = norm([pickContactFullName(it), pickContactOrgName(it), it.email || ""].join(" "));
      return hay.includes(qq);
    };

    const byOrg = (it) => {
      if (!orgFilterId) return true;
      return String(it.organisation_id || "") === String(orgFilterId);
    };

const byName = (it) => {
  if (!nameFilter) return true;
  const v = safeTrim(it.search_name || "");
  if (nameFilter === EMPTY_VAL) return !v;
  return v === nameFilter;
};

    const byJob = (it) => {
      if (!jobFilter) return true;
      const v = safeTrim(it.job_function || "");
      if (jobFilter === EMPTY_VAL) return !v;
      return v === jobFilter;
    };

const byPhone = (it) => {
  if (!phoneFilter) return true;
  const v = safeTrim(it.phone || "");
  if (phoneFilter === EMPTY_VAL) return !v;
  return v === phoneFilter;
};

const byMobile = (it) => {
  if (!mobileFilter) return true;
  const v = safeTrim(it.mobile || "");
  if (mobileFilter === EMPTY_VAL) return !v;
  return v === mobileFilter;
};

const byEmail = (it) => {
  if (!emailFilter) return true;
  const v = safeTrim(it.email || "");
  if (emailFilter === EMPTY_VAL) return !v;
  return v === emailFilter;
};

const byCompanyCode = (it) => {
  if (!companyCodeFilter) return true;
  const v = safeTrim(pickItemCompanyCodeName(it));
  if (companyCodeFilter === EMPTY_VAL) return !v;
  return v === companyCodeFilter;
};

    const byActive = (it) => {
      if (!activeFilter) return true;
      const parsed = parseActiveValue(it);
      if (activeFilter === "j") return parsed === null ? true : parsed === true;
      if (activeFilter === "n") return parsed === false;
      return true;
    };

return arr.filter((it) =>
  byQ(it) &&
  byOrg(it) &&
  byName(it) &&
  byJob(it) &&
  byPhone(it) &&
  byMobile(it) &&
  byEmail(it) &&
  byActive(it) &&
  byCompanyCode(it)
);
  }, [items, q, orgFilterId, nameFilter, jobFilter, phoneFilter, mobileFilter, emailFilter, activeFilter, companyCodeFilter]);

const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const defaultSort = (a, b) => cmpText(pickContactFullName(a), pickContactFullName(b));

    const customSort = (a, b) => {
      if (sort.key === "default") return defaultSort(a, b);
if (sort.key === "search_name") return dirMul * cmpText(a.search_name || "", b.search_name || "");
      if (sort.key === "org") return dirMul * cmpText(pickContactOrgName(a), pickContactOrgName(b));
      if (sort.key === "job") return dirMul * cmpText(a.job_function || "", b.job_function || "");
      if (sort.key === "phone") return dirMul * cmpText(a.phone || "", b.phone || "");
      if (sort.key === "mobile") return dirMul * cmpText(a.mobile || "", b.mobile || "");
      if (sort.key === "email") return dirMul * cmpText(a.email || "", b.email || "");
      if (sort.key === "active") return dirMul * cmpText(String(parseActiveValue(a) ? "1" : "0"), String(parseActiveValue(b) ? "1" : "0"));
      if (sort.key === "company_code") return dirMul * cmpText(pickItemCompanyCodeName(a), pickItemCompanyCodeName(b));
      return defaultSort(a, b);
    };

    copy.sort(customSort);
    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  useEffect(() => {
    if (loading || reopenContactHandledRef.current) return;

    let requestedId = "";
    try {
      requestedId = safeTrim(
        sessionStorage.getItem("staffiq_reopen_contact_id")
      );
    } catch {}

    if (!requestedId) {
      reopenContactHandledRef.current = true;
      return;
    }

    const rowIndex = sortedItems.findIndex(
      (item) => String(item.id) === String(requestedId)
    );

    const row =
      rowIndex >= 0
        ? sortedItems[rowIndex]
        : items.find((item) => String(item.id) === String(requestedId));

    if (!row) return;

    reopenContactHandledRef.current = true;

    try {
      sessionStorage.removeItem("staffiq_reopen_contact_id");
    } catch {}

    setListIndex(rowIndex >= 0 ? rowIndex : 0);
    loadItemFromRow(row);
    setMode("form");

    setTimeout(() => {
      try {
        if (rowIndex >= 0) {
          scrollRowIntoView(rowIndex);
        }

        scrollRightTop();

        if (window.innerWidth <= 768) {
          focusFirstRightFieldMobile();
        } else {
          firstNameRef.current?.focus?.();
        }
      } catch {}
    }, 100);
  }, [loading, items, sortedItems]);

  // ---------- LIST NAV HELPERS ----------
  function scrollMidTop() {
    try {
      if (midScrollRef.current) midScrollRef.current.scrollTop = 0;
    } catch {}
  }

  function scrollRightTop() {
    try {
      if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
    } catch {}
  }

  function scrollRowIntoView(idx) {
    try {
      const el = rowRefs.current?.[idx];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    } catch {}
  }

  function clampIndex(idx, len) {
    const L = Number(len || 0);
    if (L <= 0) return -1;
    let x = Number.isFinite(idx) ? idx : 0;
    if (x < 0) x = 0;
    if (x > L - 1) x = L - 1;
    return x;
  }

  function ensureCursorExists() {
    const L = sortedItems.length;
    if (L <= 0) return -1;
    const idx = listIndex < 0 ? 0 : clampIndex(listIndex, L);
    if (idx !== listIndex) setListIndex(idx);
    return idx;
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = clampIndex(start + delta, L);
      setTimeout(() => scrollRowIntoView(next), 0);
      return next;
    });
  }

  // ---------- RIGHT FORM: FOCUS ORDER ----------
  const focusOrderRefs = useMemo(
    () => [
      firstNameRef,
      initialsRef,
      prefixRef,
      lastNameRef,
      searchNameRef,
      genderRef,
      birthRef,
      salutationRef,
      attentionRef,
      orgRef,
      deptRef,
      jobRef,
      vStreetRef,
      vHouseRef,
      vAddRef,
      vPcRef,
      vCityRef,
      vProvRef,
      vCountryRef,
      cStreetRef,
      cHouseRef,
      cAddRef,
      cPcRef,
      cCityRef,
      cProvRef,
      cCountryRef,
      phoneRef,
      mobileRef,
      emailRef,
      webRef,
      invoiceRef,
      mailingRef,
      primaryRef,
      activeRef,
      remarkSmallRef,
    ],
    []
  );

  function getFocusableElements() {
    const els = focusOrderRefs.map((r) => r?.current).filter(Boolean);
    return els.filter((el) => {
      try {
        if (el.disabled) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      } catch {
        return false;
      }
    });
  }

function focusFirstRightField() {
    setTimeout(() => {
      try {
        scrollRightTop();
        firstNameRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  function focusFirstRightFieldMobile() {
    setTimeout(() => {
      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        panel.scrollTop = 0;
        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);

    if (idx === -1) idx = dir > 0 ? -1 : els.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;

    const el = els[next];
    if (!el) return;

    try {
      el.focus?.();
      el.scrollIntoView?.({ block: "nearest" });
    } catch {}
  }

  function openCurrentRowToRight() {
    const L = sortedItems.length;
    if (L <= 0) return;

    const idx = ensureCursorExists();
    if (idx < 0) return;

    const it = sortedItems[idx];
    if (!it) return;

    setListIndex(idx);
    loadItemFromRow(it);
    setMode("form");
    setRemarkEditor(false);

    focusFirstRightField();
    setTimeout(() => scrollRowIntoView(idx), 0);
  }

  function highlightItemIndex(idx) {
    const L = sortedItems.length;
    if (L <= 0) return;
    const clamped = clampIndex(idx, L);
    setListIndex(clamped);
    setTimeout(() => scrollRowIntoView(clamped), 0);
  }

  useEffect(() => {
    if (!pendingSpeechSearch) return;
    if (loading) return;

    if (sortedItems.length > 0) {
      setListIndex(0);
      setTimeout(() => {
        scrollMidTop();
        scrollRowIntoView(0);
      }, 0);
    } else {
      setListIndex(-1);
      setTimeout(() => scrollMidTop(), 0);
    }
    setPendingSpeechSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSpeechSearch, loading, sortedItems.length]);

  // ---------- ACTIES ----------
function resetRightPanelEmpty() {
  setSelectedId(null);
  setForm({ ...emptyForm, active: true });
  setTouched({ search_name: false, salutation: false, attention: false });
  setCorrTouched({
    corr_street: false,
    corr_house_number: false,
    corr_addition: false,
    corr_postcode: false,
    corr_city: false,
    corr_province: false,
    corr_country: false,
  });
  remarkDictateActiveRef.current = false;
  remarkDictateRunsRef.current = 0;
  setRemarkEditor(false);
}

function resetAllGridFilters() {
  setNameFilter("");
  setOrgFilterId("");
  setJobFilter("");
  setPhoneFilter("");
  setMobileFilter("");
  setEmailFilter("");
  setActiveFilter("j");
  setCompanyCodeFilter("");
}

function vernieuwList() {
  setQ("");
  resetAllGridFilters();
  resetRightPanelEmpty();
  setListIndex(-1);
  setSaveError(null);
  setFlash("Vernieuwd ✅");
  setHideVoiceError(true);
  setSort({ key: "search_name", dir: "asc" });
  setReloadKey((k) => k + 1);
  setMode("search");

  if (window.innerWidth > 768) {
    setTimeout(() => {
      try {
        searchRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  setTimeout(() => setFlash(""), 800);
  setTimeout(() => scrollMidTop(), 0);
}

  function afterSaveLikeRefresh() {
    setQ("");
    resetAllGridFilters();

    resetRightPanelEmpty();
    setListIndex(-1);
    setSaveError(null);

    setHideVoiceError(true);

    setSort({ key: "default", dir: "asc" });
    setReloadKey((k) => k + 1);

    setMode("search");
    setTimeout(() => searchRef.current?.focus?.(), 0);
    setTimeout(() => scrollMidTop(), 0);
  }

function newItem() {
  setSelectedId(null);

  const defaultOrgId = orgFilterId || "";
  const newForm = {
    ...emptyForm,
    organisation_id: defaultOrgId,
    company_code_id: companyCodeIdForOrganisation(orgs, defaultOrgId, ""),
    active: true,
  };

  setForm(newForm);
  setTouched({ search_name: false, salutation: false, attention: false });

  setCorrTouched({
    corr_street: false,
    corr_house_number: false,
    corr_addition: false,
    corr_postcode: false,
    corr_city: false,
    corr_province: false,
    corr_country: false,
  });

  setSaveError(null);
  setFlash("");
  setMode("form");
  setListIndex(-1);

  setHideVoiceError(true);

  remarkDictateActiveRef.current = false;
  remarkDictateRunsRef.current = 0;
  setRemarkEditor(false);

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  } else {
    focusFirstRightField();
  }
}

  async function saveItem() {
    if (isReadonly) {
    alert("Alleen-lezen modus. Opslaan niet toegestaan.");
    return;
  }
    setSaveError(null);
    setFlash("");

    ensureOption(deptOptionsKey, form.department);
    ensureOption(jobOptionsKey, form.job_function);

    ensureOption(pcOptionsKey, form.corr_postcode);
    ensureOption(cityOptionsKey, form.corr_city);
    ensureOption(provOptionsKey, form.corr_province);
    ensureOption(countryOptionsKey, form.corr_country);

    ensureOption(pcOptionsKey, form.visit_postcode);
    ensureOption(cityOptionsKey, form.visit_city);
    ensureOption(provOptionsKey, form.visit_province);
    ensureOption(countryOptionsKey, form.visit_country);

    setOptionsTick((x) => x + 1);

    const visitText = buildVisitAddressText(form) || "";

    if (!form.organisation_id) {
      setSaveError("Relatie is verplicht bij contactpersonen.");
      return;
    }

    const payload = {
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      company_code_id: companyCodeIdForOrganisation(orgs, form.organisation_id, form.company_code_id),

      first_name: form.first_name || "",
      initials: form.initials || "",
      name_prefix: form.name_prefix || "",
      last_name: form.last_name || "",

      search_name: form.search_name || "",
      title_before: form.title_before || "",
      title_after: form.title_after || "",
      gender: form.gender || "",
      birth_date: form.birth_date || null,

      department: form.department || "",
      job_function: form.job_function || "",

      salutation: form.salutation || "",
      attention: form.attention || "",

      active: !!form.active,
      is_active: !!form.active,
      actief: !!form.active,

      visit_address: visitText,
      visitAddress: visitText,
      visit_address_text: visitText,
      visitAddressText: visitText,

      visit_street: form.visit_street || "",
      visit_house_number: form.visit_house_number || "",
      visit_addition: form.visit_addition || "",
      visit_postcode: (form.visit_postcode || "").toUpperCase().replace(/\s+/g, ""),
      visit_city: form.visit_city || "",
      visit_province: form.visit_province || "",
      visit_country: form.visit_country || "",

      corr_street: form.corr_street || "",
      corr_house_number: form.corr_house_number || "",
      corr_addition: form.corr_addition || "",
      corr_postcode: (form.corr_postcode || "").toUpperCase().replace(/\s+/g, ""),
      corr_city: form.corr_city || "",
      corr_province: form.corr_province || "",
      corr_country: form.corr_country || "",

      phone: form.phone || "",
      mobile: form.mobile || "",
      email: form.email || "",
      website: form.website || "",

      invoice_digital: !!form.invoice_digital,
      mailing_list: !!form.mailing_list,
      is_primary: !!form.is_primary,

      remarks: form.remarks || "",
    };

    const isUpdate = formHasId();
    const method = isUpdate ? "PUT" : "POST";
    const urls = isUpdate ? [`/api/contacts/${form.id}`] : ["/api/contacts"];

    try {
      const saved = await tryFetchJson(urls, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (saved && typeof saved === "object") {
        setItems((prev) => upsertById(prev, saved));
      }

      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);

      afterSaveLikeRefresh();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonly) {
    alert("Alleen-lezen modus. Verwijderen niet toegestaan.");
    return;
  }
    setSaveError(null);
    setFlash("");

    if (!formHasId()) return;
    const ok = window.confirm("Weet je zeker dat je dit contact wilt verwijderen?");
    if (!ok) return;

    const id = form.id;
    try {
      await tryFetchJson([`/api/contacts/${id}`], { method: "DELETE" });

      setItems((prev) => removeById(prev, id));
      resetRightPanelEmpty();
      setListIndex(-1);

      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      setTimeout(() => scrollMidTop(), 0);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  // ---------- SPEECH HELPERS ----------
  function inFormContext() {
    if (remarkEditor) return true;
    if (mode === "form") return true;
    try {
      const el = document.activeElement;
      if (!el) return false;
      return rightScrollRef.current?.contains?.(el) || false;
    } catch {
      return false;
    }
  }

  function punctMap(t) {
    const x = norm(t);
    if (x === "punt") return ".";
    if (x === "komma") return ",";
    if (x === "dubbele punt" || x === "dubbelpunt") return ":";
    if (x === "puntkomma") return ";";
    if (x === "vraagteken") return "?";
    if (x === "uitroepteken") return "!";
    if (x === "nieuwe regel" || x === "enter" || x === "nieuwe regel maken") return "\n";
    return null;
  }

  function applyDictationToFocusedField(raw0) {
    const el = document.activeElement;

    if (!el) return false;

    if (el === remarkSmallRef.current || el === remarkBigRef.current) {
    return false;
}

    const containerHas = rightScrollRef.current?.contains?.(el);
    if (!containerHas) return false;

    const tag = (el.tagName || "").toLowerCase();
    const type = (el.getAttribute?.("type") || "").toLowerCase();

    const refMap = [
      ["first_name", firstNameRef],
      ["initials", initialsRef],
      ["name_prefix", prefixRef],
      ["last_name", lastNameRef],
      ["search_name", searchNameRef],
      ["gender", genderRef],
      ["birth_date", birthRef],
      ["salutation", salutationRef],
      ["attention", attentionRef],
      ["organisation_id", orgRef],
      ["department", deptRef],
      ["job_function", jobRef],
      ["visit_street", vStreetRef],
      ["visit_house_number", vHouseRef],
      ["visit_addition", vAddRef],
      ["visit_postcode", vPcRef],
      ["visit_city", vCityRef],
      ["visit_province", vProvRef],
      ["visit_country", vCountryRef],
      ["corr_street", cStreetRef],
      ["corr_house_number", cHouseRef],
      ["corr_addition", cAddRef],
      ["corr_postcode", cPcRef],
      ["corr_city", cCityRef],
      ["corr_province", cProvRef],
      ["corr_country", cCountryRef],
      ["phone", phoneRef],
      ["mobile", mobileRef],
      ["email", emailRef],
      ["website", webRef],
      ["invoice_digital", invoiceRef],
      ["mailing_list", mailingRef],
      ["is_primary", primaryRef],
      ["active", activeRef],
      ["remarks", remarkSmallRef],
    ];

    let key = "";
    for (const [k, r] of refMap) {
      if (r?.current === el) {
        key = k;
        break;
      }
    }
    if (!key) return false;

    // punctuation eerst
    const punct = punctMap(raw0);
    if (punct !== null) {
      if (tag === "input" && type === "checkbox") return false;
      if (tag === "select") return false;

      if (key === "remarks") {
        setForm((prev) => {
          const cur = String(prev.remarks || "");
          const add =
            punct === "\n" ? "\n" : (cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "") + punct + " ";
          return { ...prev, remarks: cur + add };
        });
        return true;
      }

      setForm((prev) => ({ ...prev, [key]: punct === "\n" ? "" : punct }));
      return true;
    }

    // checkbox
    if (tag === "input" && type === "checkbox") {
      const yn = parseYesNo(raw0);
      if (yn === null) return false;
      setForm((prev) => ({ ...prev, [key]: yn }));
      return true;
    }

    // gender select
    const t = norm(raw0);
    if (key === "gender") {
      if (["m", "man"].includes(t)) return setForm((p) => ({ ...p, gender: "m" })), true;
      if (["v", "vrouw"].includes(t)) return setForm((p) => ({ ...p, gender: "v" })), true;
      if (["o", "overig"].includes(t)) return setForm((p) => ({ ...p, gender: "o" })), true;
      if (isEmptyWord(raw0)) return setForm((p) => ({ ...p, gender: "" })), true;
      return false;
    }

    // leegmaken
    if (isEmptyWord(raw0)) {
      setForm((prev) => ({ ...prev, [key]: "" }));
      return true;
    }

    // ✅ veld-specifieke normalisatie
    if (key === "first_name") {
      setForm((prev) => ({ ...prev, first_name: formatFirstName(raw0) }));
      return true;
    }

    if (key === "initials") {
      setForm((prev) => ({ ...prev, initials: formatInitials(raw0) }));
      return true;
    }

    if (key === "birth_date") {
      const iso = parseBirthDateToISO(raw0);
      if (!iso) return false;
      setForm((prev) => ({ ...prev, birth_date: iso }));
      return true;
    }

    if (key === "visit_house_number" || key === "corr_house_number") {
      setForm((prev) => ({ ...prev, [key]: sanitizeHouseNumber(raw0) }));
      return true;
    }

    if (key === "phone" || key === "mobile") {
      setForm((prev) => ({ ...prev, [key]: sanitizePhone(raw0) }));
      return true;
    }

    // remarks: achteraan toevoegen (bewust zo gelaten)
    if (key === "remarks") {
      setForm((prev) => {
        const cur = String(prev.remarks || "");
        const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
        return { ...prev, remarks: cur + sep + raw0 };
      });
      return true;
    }

    // default: overschrijven
    setForm((prev) => ({ ...prev, [key]: raw0 }));
    return true;
  }

  // ---------- SPEECH: COMMAND ROUTER ----------
 
  function preserveRightScrollThen(fn) {
  const sc = rightScrollRef.current?.scrollTop ?? 0;
  fn();
  setTimeout(() => {
    try {
      if (rightScrollRef.current) rightScrollRef.current.scrollTop = sc;
    } catch {}
  }, 0);
}

function closeRemarkEditorTo(ref, { scrollNearest = false } = {}) {
  const panel = rightScrollRef.current;
  const panelTop = panel ? panel.scrollTop : 0;

  setRemarkEditor(false);
  remarkDictateActiveRef.current = false;
  remarkDictateRunsRef.current = 0;

  requestAnimationFrame(() => {
    try {
      if (panel) panel.scrollTop = panelTop;
    } catch {}

    requestAnimationFrame(() => {
      try {
        const el = ref?.current;
        if (!el) return;

        if (ref === remarkSmallRef) {
          ignoreNextRemarkSmallFocusRef.current = true;
        }

        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }

        if (panel) {
          try {
            const top = el.offsetTop;
            const bottom = top + el.offsetHeight;
            const viewTop = panel.scrollTop;
            const viewBottom = viewTop + panel.clientHeight;
            const pad = 8;

            if (top < viewTop + pad) panel.scrollTop = Math.max(0, top - pad);
            else if (bottom > viewBottom - pad) panel.scrollTop = Math.max(0, bottom - panel.clientHeight + pad);
          } catch {}
        }

        if (scrollNearest) {
          try {
            el.scrollIntoView?.({ block: "nearest" });
          } catch {}
        }
      } catch {}
    });
  });
}

function openRemarkEditorAndFocusEnd() {
  preserveRightScrollThen(() => {
    setForm((prev) => {
      const cur = String(prev.remarks || "");
      if (!cur) return prev;
      if (cur.endsWith("\n")) return prev;
      return { ...prev, remarks: cur + "\n" };
    });

    setRemarkEditor(true);
    remarkDictateActiveRef.current = false;
    remarkDictateRunsRef.current = 0;
  });

  setTimeout(() => {
    try {
      const el = remarkBigRef.current;
      if (!el) return;
      try {
        el.focus?.({ preventScroll: true });
      } catch {
        el.focus?.();
      }
      const len = String(el.value ?? "").length;
      el.setSelectionRange?.(len, len);
    } catch {}
  }, 0);
}
 
  function handleVoiceCommandLike(raw) {
const t = norm(raw);
    const parts = t.split(" ").filter(Boolean);
    const w1 = parts[0] || "";

    if (w1 === "volgende") {
      if (inFormContext()) focusNextField(+1);
      else moveCursor(+1);
      return true;
    }
    if (w1 === "vorige") {
      if (inFormContext()) focusNextField(-1);
      else moveCursor(-1);
      return true;
    }

    if (w1 === "terug" || w1 === "links") {
      if (remarkEditor) {
        setRemarkEditor(false);
        remarkDictateActiveRef.current = false;
        remarkDictateRunsRef.current = 0;
        setTimeout(() => {
          try {
            remarkSmallRef.current?.focus?.();
          } catch {}
        }, 0);
        return true;
      }

      resetRightPanelEmpty();
      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      return true;
    }

    if (w1 === "rechts" || w1 === "selecteer" || w1 === "selecteren") {
      openCurrentRowToRight();
      return true;
    }

    if (w1 === "item") {
      const n = parseInt(parts?.[1] || "", 10);
      if (Number.isFinite(n) && n > 0) {
        highlightItemIndex(n - 1);
        if (parts.includes("rechts") || parts.includes("selecteer") || parts.includes("selecteren")) {
          setTimeout(() => openCurrentRowToRight(), 0);
        }
        return true;
      }
      openCurrentRowToRight();
      return true;
    }

    if (w1 === "nieuw" || w1 === "nieuwe") return newItem(), true;
    if (w1 === "vernieuw" || w1 === "vernieuwen") return vernieuwList(), true;
    if (w1 === "opslaan" || w1 === "bewaren") return saveItem(), true;
    if (w1 === "verwijder" || w1 === "verwijderen" || w1 === "delete") return deleteItem(), true;

    return false;
  }

  // ---------- SPEECH ----------
  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 8500, // ✅ meer tijd (handig voor telefoon/mobiel)
    onCommand: (cmd, rawHeard) => {
      const raw = String(rawHeard || "").trim();

      if (raw) {
setSpeechInfo(`🎤 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      if (raw && handleVoiceCommandLike(raw)) return true;

      if (!cmd || !cmd.type) return false;

      if (
        remarkDictateActiveRef.current &&
        (cmd.type === "refresh" || cmd.type === "save" || cmd.type === "delete" || cmd.type === "new" || cmd.type === "nieuw")
      ) {
        remarkDictateActiveRef.current = false;
        remarkDictateRunsRef.current = 0;
      }

      if (cmd.type === "new" || cmd.type === "nieuw") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;

      return false;
    },

    onDictation: (text) => {
      const raw0 = String(text || "").trim();
      if (!raw0) return;

      setSpeechInfo(`🎙 ${raw0}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw0);

if (remarkDictateActiveRef.current) {
  setForm((prev) => {
    const cur = String(prev.remarks || "");
    const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";

let txt = text
      .replace(/\bnieuwe regel\b/gi, "\n")
      .replace(/\bpuntkomma\b/gi, ";")
      .replace(/\bdubbele punt\b/gi, ":")
      .replace(/\buitroepteken\b/gi, "!")
      .replace(/\bvraagteken\b/gi, "?")
      .replace(/\bkomma\b/gi, ",")
      .replace(/\bpunt\b/gi, ".")
      .replace(/\s+([.,!?;:])/g, "$1");

    txt = txt.replace(/(^|[\n.!?]\s*)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());

    return { ...prev, remarks: cur + sep + txt };
  });
  return;
}

      if (handleVoiceCommandLike(raw0)) return;

      if (inFormContext()) {
        const ok = applyDictationToFocusedField(raw0);
        if (ok) return;
        return;
      }

      if (mode === "search") {
        if (isEmptyWord(raw0)) {
          setQ("");
          setPendingSpeechSearch(true);
        } else {
          setQ(raw0);
          setPendingSpeechSearch(true);
        }
      }
    },
  });

  useEffect(() => {
    const cur = String(voice.error || "");
    const prev = String(prevVoiceErrorRef.current || "");
    if (cur && cur !== prev) {
      setHideVoiceError(false);
      prevVoiceErrorRef.current = cur;
    }
    if (!cur) prevVoiceErrorRef.current = "";
  }, [voice.error]);

  function startSpeakSmart() {
    if (!voice.supported || voice.listening) return;
    const inRemark = mode === "form" && remarkEditor;

    if (inRemark) {
      remarkDictateActiveRef.current = true;
      remarkDictateRunsRef.current = 0;
    } else {
      remarkDictateActiveRef.current = false;
      remarkDictateRunsRef.current = 0;
    }

    voice.startOnce();
  }

  useEffect(() => {
    if (!remarkEditor) return;
    setTimeout(() => {
      try {
        remarkBigRef.current?.focus?.();
      } catch {}
    }, 0);
  }, [remarkEditor]);

// ---------- LAYOUT ----------
  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 940;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

const colWidths = {
  name: 60,
  org: 60,
  job: 50,
  phone: 55,
  mobile: 55,
  email: 52,
  active: 24,
  companyCode: 55,
};

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";

  const checkRowStyle = (focused) => ({
    display: "flex",
    gap: 8,
    alignItems: "center",
    padding: "6px 8px",
    borderRadius: 6,
    background: focused ? "rgba(47, 125, 209, 0.10)" : "transparent",
    outline: focused ? "2px solid rgba(47, 125, 209, 0.35)" : "none",
  });

  const checkStyle = (focused) => ({
    transform: "scale(1.1)",
    outline: focused ? "3px solid rgba(47, 125, 209, 0.45)" : "none",
    outlineOffset: 2,
  });

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  const deleteEnabled = formHasId();
  const ageLabel = calcAgeLabel(form.birth_date);

  // ---------- RENDER ----------
  return (
    <div>
<div
  style={{
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
    columnGap: GAP,
    rowGap: isMobile ? 22 : 0,
    alignItems: "start",
    minWidth: 0,
  }}
>
        {/* LINKS */}
        <div style={{ minWidth: 0 }}>

<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 0,
    marginBottom: 10,
  }}
>
  <h1 style={{ margin: 0 }}>
    Contactpersonen <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
  </h1>

  <HelpButton
    topic="contactpersonen"
    title="Help over Contactpersonen"
  />
</div>

<>
  {!isMobile ? (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
      <input
        ref={searchRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setMode("search");
          setRemarkEditor(false);
        }}
        placeholder="Zoek…"
        style={searchInputStyle}
        onFocus={() => {
          setMode("search");
          setRemarkEditor(false);
        }}
      />

      <div
        title={`Aantal regels in het grid: ${gridCount}`}
        style={{
          minWidth: 52,
          height: 32,
          padding: "0 10px",
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          background: "#f7fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          color: "#1f4e79",
          boxSizing: "border-box",
          whiteSpace: "nowrap",
        }}
      >
        {gridCount}
      </div>

      <button
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        onClick={startSpeakSmart}
        disabled={!voice.supported || voice.listening}
        style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
      >
        {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
      </button>

      <button
        onClick={newItem}
        disabled={isReadonly}
        style={
          isReadonly
            ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
            : buttonStyle
        }
      >
        ➕ Nieuw
      </button>

      <button onClick={vernieuwList} style={buttonStyle}>
        🔄 Vernieuw
      </button>
    </div>
  ) : (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 58px",
          gap: 6,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setMode("search");
            setRemarkEditor(false);
          }}
          placeholder="Zoek…"
          style={{
            ...searchInputStyle,
            width: "100%",
            minWidth: 0,
          }}
        />

        <div
          title={`Aantal regels in het grid: ${gridCount}`}
          style={{
            height: 34,
            minHeight: 34,
            padding: "0 8px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            background: "#f7fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            color: "#1f4e79",
            boxSizing: "border-box",
            whiteSpace: "nowrap",
            lineHeight: "1",
          }}
        >
          {gridCount}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 6,
        }}
      >
        <button
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={startSpeakSmart}
          disabled={!voice.supported || voice.listening}
          style={{
            ...(!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle),
            padding: "10px 8px",
            fontSize: 13,
          }}
        >
          {voice.listening ? "🎤..." : "🎤 Spreek"}
        </button>

        <button
          onClick={newItem}
          disabled={isReadonly}
          style={{
            ...(isReadonly
              ? { ...buttonStyle, opacity: 0.5 }
              : buttonStyle),
            padding: "10px 8px",
            fontSize: 13,
          }}
        >
          ➕ Nieuw
        </button>

        <button
          onClick={vernieuwList}
          style={{
            ...buttonStyle,
            padding: "10px 8px",
            fontSize: 13,
          }}
        >
          🔄 Vernieuw
        </button>
      </div>
    </>
  )}
</>

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden…</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
            {voice.error && !hideVoiceError && <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>}
          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                ref={midScrollRef}
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={tableStyle}>
                    <colgroup>
  <col style={{ width: colWidths.name }} />
  <col style={{ width: colWidths.org }} />
  <col style={{ width: colWidths.job }} />
  <col style={{ width: colWidths.phone }} />
  <col style={{ width: colWidths.mobile }} />
  <col style={{ width: colWidths.email }} />
  <col style={{ width: colWidths.active }} />
  <col style={{ width: colWidths.companyCode }} />
</colgroup>

                    <thead>
                      <tr>
                        <th style={thClickable} onClick={() => sortToggle("search_name")}>
                          Zoeknaam{sortArrow("name")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("org")}>
                          Relatie{sortArrow("org")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("job")}>
                          Functie{sortArrow("job")}
                        </th>
<th style={thClickable} onClick={() => sortToggle("phone")}>
  Telefoon{sortArrow("phone")}
</th>
<th style={thClickable} onClick={() => sortToggle("mobile")}>
  Mobiel{sortArrow("mobile")}
</th>
<th style={thClickable} onClick={() => sortToggle("email")}>
  E-mail{sortArrow("email")}
</th>
                        <th style={thClickable} onClick={() => sortToggle("active")}>
                          Actief{sortArrow("active")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("company_code")}>
                          Bedrijfscode{sortArrow("company_code")}
                        </th>
                      </tr>

                      <tr>
                        <th style={filterThStyle}>
                          <select value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {nameHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {nameOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={orgFilterId || ""} onChange={(e) => setOrgFilterId(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {orgs.map((o) => (
                              <option key={o.id} value={String(o.id)}>
                                {pickOrgName(o)}
                              </option>
                            ))}
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {jobHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {jobColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

<th style={filterThStyle}>
  <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {phoneHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {phoneColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

<th style={filterThStyle}>
  <select value={mobileFilter} onChange={(e) => setMobileFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {mobileHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {mobileColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

<th style={filterThStyle}>
  <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {emailHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {emailColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

                        <th style={filterThStyle}>
                          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            <option value="j">j</option>
                            <option value="n">n</option>
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={companyCodeFilter} onChange={(e) => setCompanyCodeFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {companyCodeHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {companyCodeColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        const isSelected = selectedId === it.id;

                        let bg = "transparent";
                        if (isSelected) bg = rowBgSelected;
                        else if (isCursor) bg = rowBgCursor;

const nm = it.search_name || pickContactFullName(it) || "-";
const orgName = pickContactOrgName(it) || "-";
const job = it.job_function || "-";
const phone = it.phone || "-";
const mobile = it.mobile || "-";
const email = it.email || "-";
const companyCodeName = pickItemCompanyCodeName(it) || "-";
                        const act = parseActiveValue(it);
                        const checked = act === null ? true : !!act;

                        return (
                          <tr
                            key={it.id}
                            ref={(el) => (rowRefs.current[idx] = el)}
onClick={() => {
  setListIndex(idx);
  loadItemFromRow(it);
  setMode("form");
  setRemarkEditor(false);

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  } else {
    focusFirstRightField();
  }
}}
                            style={{ cursor: "pointer", backgroundColor: bg }}
                          >
                            <td style={tdStyle} title={nm}>
                              {nm}
                            </td>
                            <td style={tdStyle} title={orgName}>
                              {orgName}
                            </td>
<td style={tdStyle} title={String(job || "")}>
  {job}
</td>
<td style={tdStyle} title={String(phone || "")}>
  {phone && phone !== "-" ? (
    <button
      type="button"
      onClick={(e) => handlePhoneGridClick(e, phone)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen: ${phone}`}
    >
      {phone}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={String(mobile || "")}>
  {mobile && mobile !== "-" ? (
    <button
      type="button"
      onClick={(e) => handleMobileGridClick(e, mobile, {
        company_code_id: it.company_code_id || companyCodeIdForOrganisation(orgs, it.organisation_id, ""),
        company_code_name: pickItemCompanyCodeName(it) || companyCodeNameForOrganisation(orgs, it.organisation_id, ""),
        bedrijfsnaam: pickItemCompanyCodeName(it) || companyCodeNameForOrganisation(orgs, it.organisation_id, ""),
        relatie: pickContactOrgName(it) || "",
        contactpersoon: pickContactFullName(it) || "",
      })}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen / SMS / WhatsApp: ${mobile}`}
    >
      {mobile}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={String(email || "")}>
  {email && email !== "-" ? (
    <button
      type="button"
      onClick={(e) => handleEmailGridClick(e, email, {
        company_code_id: it.company_code_id || companyCodeIdForOrganisation(orgs, it.organisation_id, ""),
        company_code_name: pickItemCompanyCodeName(it) || companyCodeNameForOrganisation(orgs, it.organisation_id, ""),
        bedrijfsnaam: pickItemCompanyCodeName(it) || companyCodeNameForOrganisation(orgs, it.organisation_id, ""),
        relatie: pickContactOrgName(it) || "",
        contactpersoon: pickContactFullName(it) || "",
      })}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`E-mailen: ${email}`}
    >
      {email}
    </button>
  ) : (
    "-"
  )}
</td>
                            <td style={{ ...tdStyle, textAlign: "center" }} title={checked ? "j" : "n"}>
                              <input type="checkbox" checked={checked} readOnly />
                            </td>
                            <td style={tdStyle} title={companyCodeName}>
                              {companyCodeName}
                            </td>
                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten (filters te streng?)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RECHTS */}
<div
  ref={rightScrollRef}
  style={{
    width: isMobile ? "100%" : 320,
    minWidth: isMobile ? 0 : 320,
    maxWidth: isMobile ? "100%" : 320,
    position: isMobile ? "relative" : "sticky",
    top: isMobile ? "auto" : 0,
    alignSelf: "start",
    maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
    overflowY: isMobile ? "visible" : "auto",
    background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
    borderRadius: 10,
    padding: 10,
    boxSizing: "border-box",
    marginTop: isMobile ? 6 : 0,
    paddingTop: isMobile ? 6 : 10,
  }}
>
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box" }}>
            <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Contact</span>

              {remarkEditor && (
<button
  onClick={() => {
    closeRemarkEditorTo(remarkSmallRef);
  }}
  style={buttonStyle}
  title="Terug naar normaal formulier"
>
  ↩ Terug
</button>
              )}
            </h3>

            {remarkEditor ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Opmerking-editor (spreek: door-dicteren) — zeg <b>stop</b> of <b>klaar</b> om te stoppen.
                </div>

                <textarea
                  ref={remarkBigRef}
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    minHeight: "60vh",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                  onBlur={() => {
                    remarkDictateActiveRef.current = false;
                    remarkDictateRunsRef.current = 0;
                  }}
                />

<div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
<button
  onClick={saveItem}
  disabled={isFormLocked}
  style={
    isFormLocked
      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#2f7dd1")
  }
>
  💾 Opslaan
</button>

<button
  onClick={deleteItem}
  disabled={isFormLocked || !deleteEnabled}
  style={
    isFormLocked || !deleteEnabled
      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#d64545")
  }
>
  🗑 Verwijderen
</button>
</div>
              </div>
) : (
              <fieldset
                disabled={isFormLocked}
                style={{
                  border: "none",
                  padding: 0,
                  margin: 0,
                  minWidth: 0,
                }}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ fontSize: 13, color: "#333" }}>Voornaam</label>
                  <input
                    ref={firstNameRef}
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: formatFirstName(e.target.value) })}
                    style={inputStyle}
                  />

                <label style={{ fontSize: 13, color: "#333" }}>Voorletters</label>
<input
  ref={initialsRef}
  value={form.initials}
  onChange={(e) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z.]/g, "");
    setForm({ ...form, initials: v });
  }}
  onBlur={(e) => {
    setForm({ ...form, initials: formatInitials(e.target.value) });
  }}
  style={inputStyle}
/>

                <label style={{ fontSize: 13, color: "#333" }}>Tussenvoegsel(s)</label>
                <input ref={prefixRef} value={form.name_prefix} onChange={(e) => setForm({ ...form, name_prefix: e.target.value })} style={inputStyle} />

                <label style={{ fontSize: 13, color: "#333" }}>Achternaam</label>
                <input ref={lastNameRef} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} style={inputStyle} />

                <label style={{ fontSize: 13, color: "#333" }}>Zoeknaam</label>
                <input
                  ref={searchNameRef}
                  value={form.search_name}
                  onChange={(e) => {
                    setTouched((x) => ({ ...x, search_name: true }));
                    setForm({ ...form, search_name: e.target.value });
                  }}
                  style={inputStyle}
                />

                <label style={{ fontSize: 13, color: "#333" }}>Geslacht (m/v/o)</label>
                <select ref={genderRef} value={genderLabel(form.gender)} onChange={(e) => setForm({ ...form, gender: e.target.value })} style={selectStyle}>
                  <option value="">(leeg)</option>
                  <option value="m">m</option>
                  <option value="v">v</option>
                  <option value="o">o</option>
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Geboortedatum</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input ref={birthRef} type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} style={{ ...inputStyle, width: "60%" }} />
                  <div style={{ opacity: 0.75, fontSize: 13, width: "40%", textAlign: "right" }}>{ageLabel ? `(${ageLabel})` : ""}</div>
                </div>

                <label style={{ fontSize: 13, color: "#333" }}>Aanhef</label>
                <input
                  ref={salutationRef}
                  value={form.salutation}
                  onChange={(e) => {
                    setTouched((x) => ({ ...x, salutation: true }));
                    setForm({ ...form, salutation: e.target.value });
                  }}
                  style={inputStyle}
                />

                <label style={{ fontSize: 13, color: "#333" }}>Aanschrijving</label>
                <input
                  ref={attentionRef}
                  value={form.attention}
                  onChange={(e) => {
                    setTouched((x) => ({ ...x, attention: true }));
                    setForm({ ...form, attention: e.target.value });
                  }}
                  style={inputStyle}
                />

                <hr style={{ margin: "10px 0" }} />

                <label style={{ fontSize: 13, color: "#333" }}>Relatie *</label>
                <select
                  ref={orgRef}
                  value={form.organisation_id}
                  onChange={(e) => {
                    const organisationId = e.target.value;
                    setForm({
                      ...form,
                      organisation_id: organisationId,
                      company_code_id: companyCodeIdForOrganisation(orgs, organisationId, ""),
                    });
                  }}
                  style={selectStyle}
                >
                  <option value="">Kies relatie...</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {pickOrgName(o)}
                    </option>
                  ))}
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Bedrijfscode</label>
                <input
                  value={companyCodeNameForOrganisation(orgs, form.organisation_id, form.company_code_name || "") || ""}
                  readOnly
                  style={{ ...inputStyle, background: "#f7f7f7" }}
                />

                <label style={{ fontSize: 13, color: "#333" }}>Afdeling (keuzelijst)</label>
                <input
                  ref={deptRef}
                  list="cp_department_list"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(deptOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_department_list">{departmentOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Functie (keuzelijst)</label>
                <input
                  ref={jobRef}
                  list="cp_job_list"
                  value={form.job_function}
                  onChange={(e) => setForm({ ...form, job_function: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(jobOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_job_list">{jobFunctionOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <hr style={{ margin: "10px 0" }} />

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Bezoekadres</div>

                <label style={{ fontSize: 13, color: "#333" }}>Straat</label>
                <input ref={vStreetRef} value={form.visit_street} onChange={(e) => setForm({ ...form, visit_street: e.target.value })} style={inputStyle} />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Huisnummer</label>
                    <input ref={vHouseRef} value={form.visit_house_number} onChange={(e) => setForm({ ...form, visit_house_number: sanitizeHouseNumber(e.target.value) })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Toevoeging</label>
                    <input ref={vAddRef} value={form.visit_addition} onChange={(e) => setForm({ ...form, visit_addition: e.target.value })} style={inputStyle} />
                  </div>
                  <div />
                </div>

                <label style={{ fontSize: 13, color: "#333" }}>Postcode (keuzelijst)</label>
                <input
                  ref={vPcRef}
                  list="cp_visit_pc_list"
                  value={form.visit_postcode}
                  onChange={(e) => setForm({ ...form, visit_postcode: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(pcOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_visit_pc_list">{postcodeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Plaats (keuzelijst)</label>
                <input
                  ref={vCityRef}
                  list="cp_visit_city_list"
                  value={form.visit_city}
                  onChange={(e) => setForm({ ...form, visit_city: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(cityOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_visit_city_list">{cityOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Provincie (keuzelijst)</label>
                <input
                  ref={vProvRef}
                  list="cp_visit_prov_list"
                  value={form.visit_province}
                  onChange={(e) => setForm({ ...form, visit_province: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(provOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_visit_prov_list">{provinceOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Land (keuzelijst)</label>
                <input
                  ref={vCountryRef}
                  list="cp_visit_country_list"
                  value={form.visit_country}
                  onChange={(e) => setForm({ ...form, visit_country: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(countryOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_visit_country_list">{countryOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <hr style={{ margin: "10px 0" }} />

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Correspondentieadres</div>

                <label style={{ fontSize: 13, color: "#333" }}>Straat</label>
                <input
                  ref={cStreetRef}
                  value={form.corr_street}
                  onChange={(e) => {
                    setCorrTouched((x) => ({ ...x, corr_street: true }));
                    setForm({ ...form, corr_street: e.target.value });
                  }}
                  style={inputStyle}
                />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Huisnummer</label>
                    <input
                      ref={cHouseRef}
                      value={form.corr_house_number}
                      onChange={(e) => {
                        setCorrTouched((x) => ({ ...x, corr_house_number: true }));
                        setForm({ ...form, corr_house_number: sanitizeHouseNumber(e.target.value) });
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Toevoeging</label>
                    <input
                      ref={cAddRef}
                      value={form.corr_addition}
                      onChange={(e) => {
                        setCorrTouched((x) => ({ ...x, corr_addition: true }));
                        setForm({ ...form, corr_addition: e.target.value });
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div />
                </div>

                <label style={{ fontSize: 13, color: "#333" }}>Postcode (keuzelijst)</label>
                <input
                  ref={cPcRef}
                  list="cp_pc_list"
                  value={form.corr_postcode}
                  onChange={(e) => {
                    setCorrTouched((x) => ({ ...x, corr_postcode: true }));
                    setForm({ ...form, corr_postcode: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(pcOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_pc_list">{postcodeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Plaats (keuzelijst)</label>
                <input
                  ref={cCityRef}
                  list="cp_city_list"
                  value={form.corr_city}
                  onChange={(e) => {
                    setCorrTouched((x) => ({ ...x, corr_city: true }));
                    setForm({ ...form, corr_city: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(cityOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_city_list">{cityOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Provincie (keuzelijst)</label>
                <input
                  ref={cProvRef}
                  list="cp_prov_list"
                  value={form.corr_province}
                  onChange={(e) => {
                    setCorrTouched((x) => ({ ...x, corr_province: true }));
                    setForm({ ...form, corr_province: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(provOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_prov_list">{provinceOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Land (keuzelijst)</label>
                <input
                  ref={cCountryRef}
                  list="cp_country_list"
                  value={form.corr_country}
                  onChange={(e) => {
                    setCorrTouched((x) => ({ ...x, corr_country: true }));
                    setForm({ ...form, corr_country: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(countryOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="cp_country_list">{countryOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <hr style={{ margin: "10px 0" }} />

                <label style={{ fontSize: 13, color: "#333" }}>Telefoonnummer</label>
                <input
  ref={phoneRef}
  value={form.phone}
  onChange={(e) => {
    const v = e.target.value;
    if (/^\+?[0-9-\s]*$/.test(v)) {
      setForm({ ...form, phone: v });
    }
  }}
  style={inputStyle}
/>

                <label style={{ fontSize: 13, color: "#333" }}>Mobiel nummer</label>
 <input
  ref={mobileRef}
  value={form.mobile}
  onChange={(e) => {
    const v = e.target.value;
    if (/^\+?[0-9-\s]*$/.test(v)) {
      setForm({ ...form, mobile: v });
    }
  }}
  style={inputStyle}
/>

                <label style={{ fontSize: 13, color: "#333" }}>E-mail adres</label>
                <input ref={emailRef} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />

                <label style={{ fontSize: 13, color: "#333" }}>Internet adres</label>
                <input ref={webRef} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={inputStyle} />

                <label style={checkRowStyle(focusedKey === "invoice_digital")}>
                  <input
                    ref={invoiceRef}
                    type="checkbox"
                    checked={!!form.invoice_digital}
                    onChange={(e) => setForm({ ...form, invoice_digital: e.target.checked })}
                    onFocus={() => setFocusedKey("invoice_digital")}
                    onBlur={() => setFocusedKey("")}
                    style={checkStyle(focusedKey === "invoice_digital")}
                  />
                  Ontvangt facturen digitaal (j/n)
                </label>

                <label style={checkRowStyle(focusedKey === "mailing_list")}>
                  <input
                    ref={mailingRef}
                    type="checkbox"
                    checked={!!form.mailing_list}
                    onChange={(e) => setForm({ ...form, mailing_list: e.target.checked })}
                    onFocus={() => setFocusedKey("mailing_list")}
                    onBlur={() => setFocusedKey("")}
                    style={checkStyle(focusedKey === "mailing_list")}
                  />
                  Mailinglijst (j/n)
                </label>

                <label style={checkRowStyle(focusedKey === "is_primary")}>
                  <input
                    ref={primaryRef}
                    type="checkbox"
                    checked={!!form.is_primary}
                    onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                    onFocus={() => setFocusedKey("is_primary")}
                    onBlur={() => setFocusedKey("")}
                    style={checkStyle(focusedKey === "is_primary")}
                  />
                  Primair contact (j/n)
                </label>

                <label style={checkRowStyle(focusedKey === "active")}>
                  <input
                    ref={activeRef}
                    type="checkbox"
                    checked={!!form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    onFocus={() => setFocusedKey("active")}
                    onBlur={() => setFocusedKey("")}
                    style={checkStyle(focusedKey === "active")}
                  />
                  Actief (j/n)
                </label>

                <hr style={{ margin: "10px 0" }} />

                <label style={{ fontSize: 13, color: "#333" }}>Opmerking</label>
<textarea
  ref={remarkSmallRef}
  value={form.remarks}
  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
  rows={6}
  style={{ ...inputStyle, resize: "vertical" }}
  onFocus={() => {
    setMode("form");

    if (ignoreNextRemarkSmallFocusRef.current) {
      ignoreNextRemarkSmallFocusRef.current = false;
      return;
    }

    openRemarkEditorAndFocusEnd();
  }}
/>

<div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
  <button
    onClick={saveItem}
    disabled={isFormLocked || isReadonly}
    style={
      isFormLocked || isReadonly
        ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#2f7dd1")
    }
  >
    💾 Opslaan
  </button>

  <button
    onClick={deleteItem}
    disabled={isFormLocked || !deleteEnabled || isReadonly}
    style={
      isFormLocked || !deleteEnabled || isReadonly
        ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#d64545")
    }
  >
    🗑 Verwijderen
  </button>
</div>

<div style={{ marginTop: 10, opacity: 0.7, fontSize: 12 }}>Contact-id: {formHasId() ? String(form.id) : "(nieuw)"}</div>
              </div>
              </fieldset>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}